import { useState, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { onboardingApi } from '../api/client'
import { track } from '../lib/tracker'
import GitHubAuthButton from '../components/GitHubAuthButton'
import { Alert, Button, Card, Field, Input } from '../components/ui'

export default function RegisterPage() {
  const { t, i18n } = useTranslation()
  const appName = 'Provisyer'
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [phone, setPhone] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError('')
    setLoading(true)
    try {
      await onboardingApi.register(email, companyName, phone, i18n.language)
      track('signup')
      setSubmitted(true)
    } catch {
      setError(t('register.error'))
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-sm text-center">
          <Card padded={false} className="p-8">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">{t('register.checkEmail')}</h2>
            <p className="text-sm text-gray-500 mb-6">{t('register.checkEmailHint', { email })}</p>
            <Link to="/login" className="text-sm text-brand-600 hover:text-brand-700 font-medium">
              {t('register.backToLogin')}
            </Link>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('register.title', { appName })}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('register.subtitle')}</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert tone="danger">{error}</Alert>}

            <Field label={t('register.email')}>
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder={t('register.emailPlaceholder')}
                />
              )}
            </Field>

            <Field label={t('register.companyName')} hint={t('register.companyNameHint')}>
              {(p) => (
                <Input
                  {...p}
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  autoComplete="organization"
                  placeholder={t('register.companyNamePlaceholder')}
                />
              )}
            </Field>

            <Field label={t('register.phone')}>
              {(p) => (
                <Input
                  {...p}
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                  placeholder={t('register.phonePlaceholder')}
                />
              )}
            </Field>

            <Button type="submit" loading={loading} fullWidth>
              {loading ? t('register.submitting') : t('register.submit')}
            </Button>

            <div className="flex items-center gap-2 my-1">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wide">{t('register.or')}</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <GitHubAuthButton mode="signup" />

            <p className="text-xs text-gray-500 text-center">
              {t('register.consent')}{' '}
              <Link to={i18n.language?.startsWith('tr') ? '/hizmet-sartlari' : '/terms'} target="_blank" className="text-brand-600 hover:text-brand-700 underline">
                {t('profileGate.termsLink')}
              </Link>
              {' '}&bull;{' '}
              <Link to={i18n.language?.startsWith('tr') ? '/gizlilik-politikasi' : '/privacy'} target="_blank" className="text-brand-600 hover:text-brand-700 underline">
                {t('profileGate.privacyLink')}
              </Link>
            </p>
          </form>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-4">
          {t('register.haveAccount')}{' '}
          <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
            {t('register.signIn')}
          </Link>
        </p>
      </div>
    </div>
  )
}
